import { forwardRef, type ReactNode } from "react";
import type { CheckboxProps as ReactAriaCheckboxProps } from "react-aria-components";
import { Checkbox as ReactAriaCheckbox } from "react-aria-components";

import { cn } from "../utils";
import { NcIconMinus, NcIconTick } from "./nc-icons";
import { NcLabelText } from "./nc-label";

export interface NcCheckboxProps extends ReactAriaCheckboxProps {
  children: ReactNode;
  hideLabel?: boolean;
}

export const NcCheckbox = forwardRef<HTMLLabelElement, NcCheckboxProps>(
  ({ children, isRequired, hideLabel, className, ...props }, ref) => {
    return (
      <ReactAriaCheckbox
        data-nc="NcCheckbox"
        ref={ref}
        className={cn(
          "group flex max-w-max items-center gap-2 rounded-md transition-transform duration-300",
          // Disabled
          "data-disabled:text-disabled data-disabled:cursor-not-allowed",
          // Focus visible
          "data-focus-visible:outline-main data-focus-visible:outline-2 data-focus-visible:outline-offset-2",
          className
        )}
        {...props}
      >
        <>
          <div
            className={cn(
              "grid h-6 w-6 min-w-0 place-items-center",
              "bg-light border-ui cursor-pointer rounded-md border transition-colors duration-250",
              // Disabled
              "group-data-disabled:bg-disabled-light",
              // Selected (checked)
              "group-data-selected:bg-active group-data-selected:text-light",
              // Selected + disabled
              "group-data-disabled:group-data-selected:bg-disabled",
              // Indeterminate
              "group-data-indeterminate:bg-active group-data-indeterminate:text-light",
              // Invalid
              "group-data-invalid:border-danger"
            )}
          >
            <NcIconTick className="h-4 scale-0 transition-all duration-300 ease-out [grid-area:1/-1] group-data-selected:scale-100" />
            <NcIconMinus className="h-3.5 scale-0 transition-all duration-300 ease-out [grid-area:1/-1] not-group-data-selected:group-data-indeterminate:scale-100" />
          </div>
          <NcLabelText isRequired={isRequired} hideLabel={hideLabel}>
            {children}
          </NcLabelText>
        </>
      </ReactAriaCheckbox>
    );
  }
);
